import { DocumentType, WorkplaceType } from "src/pages/Reports/WorkPlaceReport/types";
import { FlexCol, FlexRow, FlexRowCenter } from "src/utils/flexLayouts";
import { DoubleDownIcon } from "src/pages/Reports/shared/icons";
import { twMerge } from "tailwind-merge";
import Loading from "src/components/Loading";
import { UserAvatarStore } from "src/components/ui/avatar/Avatar";
import { useSearchParams } from "react-router-dom";
import { TooltipItem } from "src/utils/Tooltip";

type Props = {
  workplaces: (WorkplaceType & { collapsed: boolean })[];
  toggleCollapsed: (id: string) => void;
  loading: boolean;
};

const headings = [
  "Рабочее место",
  "Документ",
  "Версия",
  "Критичность",
  "% ознакомления",
  "Ознакомлены",
  "Не ознакомлены",
  "Отклонены",
];

const twTableItemGridMarkup = "grid grid-cols-[15%_16%_4%_8%_8%_15.5%_16.5%_16.5%] text-xs";
const twGridItem = "gap-[5px] justify-center items-center border-r border-dark6 p-2.5";

function Table({ workplaces, toggleCollapsed, loading }: Props) {
  const [query, setQuery] = useSearchParams();
  const onDocumentClick = (document: DocumentType) => {
    const newSearchParams = new URLSearchParams(query);

    if (document.versionId && document.mainDocumentId) {
      newSearchParams.set("version", document.versionId.toString());
      newSearchParams.set("documentCard", document.mainDocumentId.toString());
    } else {
      newSearchParams.set("documentCard", document.id);
    }

    setQuery(newSearchParams);
  };

  return (
    <div
      id="table-scroll"
      className="w-auto pb-5 pt-0 bg-white rounded-lg table-custom shadow-custom overflow-x-auto scrollbar scrollbar-h-2 scrollbar-thumb-[#D1D5DB]"
    >
      <FlexCol className="bg-white rounded-2xl min-w-[976px] w-auto mx-auto">
        <div className={twMerge("border-b border-black", twTableItemGridMarkup)}>
          {headings.map((heading) => (
            <FlexRowCenter key={heading} className="border-r border-dark6 last:border-0 p-2.5">
              <span className="text-black whitespace-nowrap">{heading}</span>
            </FlexRowCenter>
          ))}
        </div>
        {loading ? (
          <FlexRowCenter className="p-10">
            <Loading />
          </FlexRowCenter>
        ) : workplaces.length > 0 ? (
          workplaces.map((workplace) => (
            <FlexCol
              className={twMerge("border-b border-dark6", workplace.collapsed && "border-b-0")}
              key={workplace.id}
            >
              <FlexRow
                className="justify-between items-center p-2.5 border-b border-dark6 cursor-pointer"
                onClick={() => toggleCollapsed(workplace.id)}
              >
                <span className="text-sm text-black">{workplace.title}</span>
                <TooltipItem tooltipsText={workplace.collapsed ? "Развернуть" : "Свернуть"} position="left">
                  <button>
                    <DoubleDownIcon className={workplace.collapsed ? "rotate-180" : ""} />
                  </button>
                </TooltipItem>
              </FlexRow>
              {!workplace.collapsed && (
                <div className={twTableItemGridMarkup}>
                  {/* Колонка 1: Рабочее место - общая для всех документов */}
                  <FlexCol
                    className="border-r border-dark6 p-2.5 gap-[5px] justify-center items-center"
                    style={{ gridRow: `1 / span ${workplace.documents.length}` }}
                  >
                    <span className="font-medium text-black">{workplace.title}</span>
                    <span className="text-[#637381]">({workplace.familiarizationPercent}% ознакомлены)</span>
                  </FlexCol>

                  {/* Строки с документами */}
                  {workplace.documents.map((document, docIndex) => (
                    <>
                      {/* Колонка 2: Название + код документа */}
                      <FlexCol
                        key={`${document.code}-name`}
                        className={twMerge(
                          twGridItem,
                          docIndex < workplace.documents.length - 1 && "border-b",
                          "cursor-pointer",
                        )}
                        onClick={() => onDocumentClick(document)}
                      >
                        <span className="max-w-full font-medium truncate text-darkblue">{document.name}</span>
                        <span className="max-w-full truncate text-[#637381]">{document.code}</span>
                      </FlexCol>

                      {/* Колонка 3: Версия */}
                      <FlexCol
                        key={`${document.code}-version`}
                        className={twMerge(twGridItem, docIndex < workplace.documents.length - 1 && "border-b")}
                      >
                        <span>{document.version}</span>
                      </FlexCol>

                      {/* Колонка 4: Критичность */}
                      <FlexCol
                        key={`${document.code}-critical`}
                        className={twMerge(twGridItem, docIndex < workplace.documents.length - 1 && "border-b")}
                      >
                        {document.critical ? (
                          <FlexRow className="gap-1 items-center">
                            <span className="inline-block w-2 h-2 rounded-full bg-red" />
                            <span>Критичный</span>
                          </FlexRow>
                        ) : (
                          <FlexRow className="gap-1 items-center">
                            <span className="inline-block w-2 h-2 rounded-full bg-[#dfe4ea]" />
                            <span>Не критичный</span>
                          </FlexRow>
                        )}
                      </FlexCol>

                      {/* Колонка 5: Процент */}
                      <FlexCol
                        key={`${document.code}-percent`}
                        className={twMerge(twGridItem, docIndex < workplace.documents.length - 1 && "border-b")}
                      >
                        <span>{document.familiarizationPercent ?? workplace.familiarizationPercent}%</span>
                      </FlexCol>

                      {/* Колонка 6: Ознакомленные */}
                      <FlexCol
                        key={`${document.code}-familiarized`}
                        className={twMerge(
                          twGridItem,
                          "flex flex-wrap",
                          docIndex < workplace.documents.length - 1 && "border-b",
                          document.familiarizedUsers.length > 0 && "justify-start items-start",
                        )}
                      >
                        {document.familiarizedUsers.length > 0 ? (
                          document.familiarizedUsers.map((item, userIndex) => (
                            <FlexRow className="" key={`${document.id}-fam-${item.id}-${userIndex}`}>
                              <UserAvatarStore
                                image={item.img}
                                shortName={item.shortName}
                                displayName={item.name}
                                email={item.email}
                                single={true}
                                className="items-center"
                              />
                            </FlexRow>
                          ))
                        ) : (
                          <span className="text-[#637381]">—</span>
                        )}
                      </FlexCol>

                      {/* Колонка 7: Не ознакомленные */}
                      <FlexCol
                        key={`${document.code}-nonfamiliarized`}
                        className={twMerge(
                          twGridItem,
                          docIndex < workplace.documents.length - 1 && "border-b",
                          document.nonFamiliarizedUsers.length > 0 && "justify-start items-start",
                        )}
                      >
                        {document.nonFamiliarizedUsers.length > 0 ? (
                          document.nonFamiliarizedUsers.map((item, userIndex) => (
                            <FlexRow key={`${document.id}-nonfam-${item.id}-${userIndex}`}>
                              <UserAvatarStore
                                image={item.img}
                                shortName={item.shortName}
                                displayName={item.name}
                                email={item.email}
                                single={true}
                                className="items-center"
                              />
                            </FlexRow>
                          ))
                        ) : (
                          <span className="text-[#637381]">—</span>
                        )}
                      </FlexCol>

                      {/* Колонка 8: Отклоненные */}
                      <FlexCol
                        key={`${document.code}-canceled`}
                        className={twMerge(
                          twGridItem,
                          "border-r-0",
                          docIndex < workplace.documents.length - 1 && "border-b",
                          document.canceledUsers.length > 0 && "justify-start items-start",
                        )}
                      >
                        {document.canceledUsers.length > 0 ? (
                          document.canceledUsers.map((item, userIndex) => (
                            <FlexRow key={`${document.id}-canceled-${item.id}-${userIndex}`}>
                              <UserAvatarStore
                                image={item.img}
                                shortName={item.shortName}
                                displayName={item.name}
                                email={item.email}
                                single={true}
                                className="items-center"
                              />
                            </FlexRow>
                          ))
                        ) : (
                          <span className="text-[#637381]">—</span>
                        )}
                      </FlexCol>
                    </>
                  ))}
                </div>
              )}
            </FlexCol>
          ))
        ) : (
          <FlexRowCenter className="p-10">
            <span className="text-lg text-black">Нет данных</span>
          </FlexRowCenter>
        )}
      </FlexCol>
    </div>
  );
}

export default Table;
